import { rest } from 'msw';
import moment from 'moment';
import { withScenario } from '../../utils/middleware';
import { mockResponse } from '../../utils/responseResult';
import data from './5gsaController.json';

const ROOT = '/api/FiveGSAController/';
const sessions = Object.create(null);
export const resetFiveGsaMock = () => {
  Object.keys(sessions).forEach((key) => delete sessions[key]);
};

const inDateRange = (date, params) => {
  const value = moment(date, ['YYYY/MM/DD', 'YYYY-MM-DD'], true);
  const start = params.startDate && moment(params.startDate, ['YYYY/MM/DD', 'YYYY-MM-DD'], true);
  const end = params.endDate && moment(params.endDate, ['YYYY/MM/DD', 'YYYY-MM-DD'], true);
  return (!start || !value.isBefore(start, 'day')) && (!end || !value.isAfter(end, 'day'));
};

const failurePayload = (endpoint) => {
  if (endpoint === 'initFiveGSA') return { isReserved: false };
  if (endpoint === 'events') return { events: [], venues: [] };
  if (endpoint === 'venues') return { venues: [] };
  if (endpoint === 'check') return { isReserve: false };
  return {};
};

const resolver = (endpoint) => async (req, res, ctx) => {
  const config = data[ROOT + endpoint];
  const scenario = new URLSearchParams(window.location.search).get('scenario') || 'default';
  const fail = (error = config.error.default) => res(ctx.json(mockResponse(failurePayload(endpoint), false, error)));
  const transId = req.headers.get('transId');
  if (!transId || !transId.trim() || scenario === 'invalidTransId') {
    return fail({ code: 'ERR_000_000', message: 'transId is required or invalid' });
  }
  let body;
  try {
    body = typeof req.body === 'string' ? JSON.parse(req.body) : req.body;
  } catch (error) {
    return fail({ code: 'ERR_000_000', message: 'Request body must be a JSON object' });
  }
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return fail({ code: 'ERR_000_000', message: 'Request body must be a JSON object' });
  }
  if (scenario === 'error' || scenario === 'fail' || scenario === 'busy') return fail();
  if (scenario !== 'default' && config.error[scenario]) return fail(config.error[scenario]);

  const initConfig = data[ROOT + 'initFiveGSA'];
  const identity = initConfig.success[scenario] || initConfig.success.default;
  const sessionKey = JSON.stringify([transId, scenario]);
  if (!sessions[sessionKey]) {
    sessions[sessionKey] = {
      offers: identity.offers.map((offer) => ({ ...offer })),
      events: { 3002726401: 'E20261129001', 3002726403: 'E20261016001' },
    };
  }
  const state = sessions[sessionKey];
  const success = (value) => res(ctx.json(mockResponse(value)));
  const eventData = data[ROOT + 'events'].success.default.events;

  if (endpoint === 'initFiveGSA') {
    return success({ ...identity, offers: state.offers, isReserved: state.offers.length > 0 });
  }
  if (endpoint === 'events') {
    const events =
      scenario === 'empty'
        ? []
        : eventData.filter(
            (event) =>
              inDateRange(event.eventDate, body) &&
              ['type', 'eventCode', 'venueCode'].every((key) => !body[key] || event[key] === body[key])
          );
    const venues = [];
    events.forEach((event) => {
      if (!venues.some((venue) => venue.venueCode === event.venueCode)) {
        venues.push({ venueCode: event.venueCode, venueName: event.venueName });
      }
    });
    return success({ events, venues });
  }
  if (endpoint === 'venues') {
    const venues =
      scenario === 'empty'
        ? []
        : config.success.default.venues
            .filter((venue) => !body.venueCode || venue.venueCode === body.venueCode)
            .map((venue) => ({
              ...venue,
              events: venue.events.filter((event) => inDateRange(event.eventDate, body)),
              calendarDays: venue.calendarDays.filter((day) => inDateRange(day.date, body)),
            }));
    return success({ venues });
  }
  if (endpoint === 'check') {
    if (
      typeof body.offerId !== 'string' ||
      !body.offerId.trim() ||
      typeof body.startTime !== 'string' ||
      !body.startTime.trim()
    ) {
      return fail(config.error.invalid);
    }
    if (!moment(body.startTime, 'YYYY/MM/DD HH:mm', true).isValid()) {
      return success({ isReserve: false });
    }
    return success({
      isReserve:
        scenario === 'duplicate' ||
        state.offers.some(
          (offer) =>
            offer.offerId === body.offerId &&
            offer.status !== 'INACTIVE' &&
            offer.serviceDate &&
            offer.serviceDate.slice(0, 16) === body.startTime
        ),
    });
  }
  if (endpoint === 'reservation') {
    if (
      typeof body.eventCode !== 'string' ||
      !body.eventCode.trim() ||
      typeof body.offerId !== 'string' ||
      !body.offerId.trim()
    )
      return fail(config.error.invalid);
    const event = eventData.find(
      (item) => item.eventCode === body.eventCode && item.productOffer.offerId === body.offerId
    );
    if (!event || !event.available) return fail(config.error.notFound);
    const parsedStartDate =
      body.startDate && moment(body.startDate, 'YYYY-MM-DDTHH:mm:ss', true).isValid()
        ? moment(body.startDate, 'YYYY-MM-DDTHH:mm:ss', true).format('YYYY/MM/DD HH:mm:ss')
        : null;
    const parsedEndDate =
      body.endDate && moment(body.endDate, 'YYYY-MM-DDTHH:mm:ss', true).isValid()
        ? moment(body.endDate, 'YYYY-MM-DDTHH:mm:ss', true).format('YYYY/MM/DD HH:mm:ss')
        : null;
    const serviceDate = parsedStartDate || `${event.eventDate} ${event.startTime}:00`;
    const expectedEndDate = parsedEndDate || `${event.eventDate} ${event.endTime}:00`;
    if (
      !state.offers.some(
        (offer) => offer.offerId === body.offerId && offer.serviceDate === serviceDate && offer.status !== 'INACTIVE'
      )
    ) {
      state.offers.push({
        offerId: body.offerId,
        eventCode: body.eventCode,
        serviceDate,
        expectedEndDate,
        venue: event.venueName,
        eventName: body.eventName || event.title,
        status: 'FT-ACTIVE',
      });
      state.events[body.offerId] = body.eventCode;
    }
    return success(config.success.default);
  }
  if (
    typeof body.offerId !== 'string' ||
    !body.offerId.trim() ||
    typeof body.eventCode !== 'string' ||
    !body.eventCode.trim()
  ) {
    return fail(config.error.invalid);
  }
  const activeOffer = state.offers.find(
    (offer) => offer.offerId === body.offerId && (offer.status || '').toUpperCase() === 'ACTIVE'
  );
  if (activeOffer) return fail(config.error.confirmationFailed || config.error.default);
  const matches = state.offers.filter(
    (offer) => offer.offerId === body.offerId && (offer.status || '').toUpperCase() === 'FT-ACTIVE'
  );
  if (!matches.length) return fail(config.error.default);
  matches.forEach((offer) => {
    offer.status = 'INACTIVE';
  });
  return success({ eventCode: body.eventCode || state.events[body.offerId], status: 'CANCELLED' });
};

export const handlers = Object.keys(data).map((path) =>
  rest.post(`*${path}`, withScenario(resolver(path.slice(ROOT.length))))
);
